import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextVatReturn } from './VatReturn';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { VatReturnDA } from '../../../../services/admin/vat.return.service';
import { VatReturnCaseDetail } from '../../../../models/vat.return.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { resolveDocumentRoute } from '../../../../scripts/document.link.script';

import AppPreloader from '../../../../components/Preloader';

export interface VatReturnCasePageProps {};

let errorResponse: Error;

const VatReturnCasePage: React.FunctionComponent<VatReturnCasePageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextVatReturn()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const { id_period, case_code } = useParams<{ id_period: string; case_code: string }>()
  const idPeriod = id_period ?? ''

  const [mounted, setMounted] = useState(false)
  const [caseDetail, setCaseDetail] = useState<VatReturnCaseDetail | null>(null)

  const loadCaseDetail = async (id_company: string, code: string, id_period: string) => {
    await VatReturnDA.getVatReturnCase(id_company, code, id_period).then((response) => {
      if (response.status === 200) {
        setCaseDetail(response.data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleGoBack = () => {
    navigate(`/main/accounting/vat-return/view/${idPeriod}`)
  }

  useEffect(() => {
    if (companyForUser && case_code && idPeriod) {
      loadCaseDetail(companyForUser.id, case_code, idPeriod)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, case_code, idPeriod])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: `${lang.labels.vatReturn} — ${lang.labels.caseNumber} ${case_code ?? ''}`}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const currency = companyForUser?.currency?.symbol ?? companyForUser?.currency?.code ?? 'CHF'

  return (
    <div className="w-100 h-100">
      { companyForUser && caseDetail
        ?
        <>
          {/* Header card */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center">
                  <span className="badge badge-primary fw-bold fs-6 me-3">{caseDetail.case.code}</span>
                  <h4 className="m-0">{caseDetail.label}</h4>
                </div>
              </div>
              <div className="card-toolbar">
                <button className="btn btn-sm btn-light-primary" type="button" onClick={handleGoBack}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 me-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="black"/>
                        <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.back}
                  </div>
                </button>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row">
                <div className="col-12 col-md-6">
                  <table className="table gs-0 gy-1 m-0">
                    <tbody>
                      <tr>
                        <td className="w-150px fw-bold text-muted">{lang.labels.type}:</td>
                        <td className="fw-bold">
                          <span className={`badge ${caseDetail.type === 'aggregate' ? 'badge-light-info' : caseDetail.type === 'derived' ? 'badge-light-warning' : caseDetail.type === 'manual' ? 'badge-light-secondary' : 'badge-light-success'}`}>
                            {caseDetail.type}
                          </span>
                        </td>
                      </tr>
                      { caseDetail.total_base !== null && caseDetail.total_base !== undefined &&
                        <tr>
                          <td className="w-150px fw-bold text-muted">{lang.labels.base}:</td>
                          <td className="fw-bold fs-6">{currency} {formatMoney(caseDetail.total_base)}</td>
                        </tr>
                      }
                      <tr>
                        <td className="w-150px fw-bold text-muted">{lang.labels.amount}:</td>
                        <td className="fw-bold fs-6">{currency} {formatMoney(caseDetail.total_amount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Content: aggregate composition or tickets */}
          { caseDetail.type === 'aggregate' && caseDetail.components && caseDetail.components.length > 0
            ?
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h5 className="m-0">{lang.labels.vatCaseComposition}</h5>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-60px">{lang.labels.caseNumber}</th>
                        <th className="min-w-200px">{lang.labels.description}</th>
                        <th className="w-80px text-center">{lang.labels.sign}</th>
                        <th className="min-w-120px text-end">{lang.labels.amount} ({currency})</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { caseDetail.components.map((comp, index) => (
                        <tr
                          key={index}
                          className={`row-dashed ${comp.code ? 'cursor-pointer' : ''}`}
                          onClick={() => comp.code && navigate(`/main/accounting/vat-return/view/${idPeriod}/case/${comp.code}`)}
                        >
                          <td><span className="badge badge-light fw-bold fs-8">{comp.code}</span></td>
                          <td className={comp.code ? 'text-dark text-hover-primary' : ''}>{comp.label}</td>
                          <td className="text-center">
                            <span className={`badge ${comp.sign === '+' ? 'badge-light-success' : 'badge-light-danger'}`}>{comp.sign}</span>
                          </td>
                          <td className="text-end fw-bold">{formatMoney(comp.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bolder fs-6">
                        <td colSpan={3} className="text-end">{lang.labels.total}:</td>
                        <td className="text-end">{currency} {formatMoney(caseDetail.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            :
            caseDetail.tickets && caseDetail.tickets.length > 0
            ?
            (() => {
              const isLeaf = caseDetail.type === 'leaf'
              const relevantField = (t: typeof caseDetail.tickets[0]) => isLeaf ? t.base : t.amount
              const netValue = isLeaf ? caseDetail.total_base : caseDetail.total_amount

              return (
                <div className="card card-flush mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h5 className="m-0">{lang.labels.tickets} ({caseDetail.tickets.length})</h5>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed mb-0">
                        <thead className="border-bottom">
                          <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                            <th className="min-w-100px">{lang.labels.code}</th>
                            <th className="min-w-100px">{lang.labels.date}</th>
                            <th className="min-w-120px">{lang.labels.reference}</th>
                            <th className="min-w-100px text-end">{lang.labels.debit} (CHF)</th>
                            <th className="min-w-100px text-end">{lang.labels.credit} (CHF)</th>
                          </tr>
                        </thead>
                        <tbody className="text-secondary fw-bold">
                          { caseDetail.tickets.map((ticket, index) => {
                            const docRoute = resolveDocumentRoute(ticket.document?.type, ticket.document?.id)
                            const docLabel = ticket.document_number || ''
                            const val = relevantField(ticket)
                            const isDebit = ticket.postingNature === 'debit'

                            return (
                              <tr key={index} className="row-dashed">
                                <td>
                                  <span className="badge badge-light fw-bold fs-8">{ticket.code}</span>
                                </td>
                                <td className="fs-7">{dateFormat(lang.code, ticket.created_date)}</td>
                                <td>
                                  { docRoute && docLabel
                                    ?
                                    <Link to={docRoute} className="text-primary fw-bold text-hover-dark">
                                      {docLabel}
                                    </Link>
                                    :
                                    docLabel
                                    ?
                                    <span className="text-muted">{docLabel}</span>
                                    :
                                    <span className="text-muted">{'\u2014'}</span>
                                  }
                                </td>
                                <td className="text-end">{isDebit ? formatMoney(val) : ''}</td>
                                <td className="text-end">{!isDebit ? formatMoney(val) : ''}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="fw-bolder fs-7 border-top">
                            <td colSpan={3} className="text-end">{lang.labels.total}:</td>
                            <td className="text-end">{formatMoney(caseDetail.total_debit ?? 0)}</td>
                            <td className="text-end">{formatMoney(caseDetail.total_credit ?? 0)}</td>
                          </tr>
                          <tr className="fw-bolder fs-6 bg-light">
                            <td colSpan={3} className="text-end">{lang.labels.net}:</td>
                            <td colSpan={2} className="text-end">{formatMoney(netValue)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )
            })()
            :
            <div className="card card-flush">
              <div className="card-body text-center py-10">
                <span className="text-muted fw-bold">{lang.labels.noRecordsFound}</span>
              </div>
            </div>
          }
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default VatReturnCasePage;
